import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import isoWeek from 'dayjs/plugin/isoWeek'

import { IncotermGoupEnum } from '@types'

import {
  MONTHLY_COMPLIANCE_STATUS_ORDER,
  type MonthlyComplianceStatus,
} from '../features/AdminRouter/AdminDashboardMetrics/monthlyComplianceStatus'

dayjs.extend(isoWeek)

export type DashboardFiltersContextValue = {
  tripType: IncotermGoupEnum
  setTripType: (v: IncotermGoupEnum) => void
  weekStart: Date
  setWeekStart: (d: Date) => void
  selectedDay: Date
  setSelectedDay: (d: Date) => void
  /** First day of the selected month (monthly dashboard). */
  selectedMonth: Date
  setSelectedMonth: (d: Date) => void
  /** Monthly dashboard: contiguous compliance tiers (Confirmado → …). */
  monthlyComplianceStatuses: MonthlyComplianceStatus[]
  setMonthlyComplianceStatuses: (v: MonthlyComplianceStatus[]) => void
  /** Optional supplier filter (`undefined` = all). */
  supplierId?: number
  setSupplierId: (id: number | undefined) => void
  /** Optional carrier filter (`undefined` = all). */
  carrierId?: number
  setCarrierId: (id: number | undefined) => void
  /** Reset shared filters when switching Weekly ↔ Monthly modules. */
  resetFiltersToDefaults: () => void
}

const DashboardFiltersContext = createContext<DashboardFiltersContextValue | null>(null)

const mondayThisWeek = () => dayjs().isoWeekday(1).startOf('day').toDate()

const startOfCurrentMonth = () => dayjs().startOf('month').toDate()

const defaultMonthlyComplianceStatuses = (): MonthlyComplianceStatus[] => [
  ...MONTHLY_COMPLIANCE_STATUS_ORDER,
]

function getWeekRange(weekStart: Date) {
  const start = dayjs(weekStart).isoWeekday(1).startOf('day')
  const end = start.add(6, 'day').endOf('day')
  return { start, end }
}

function isDateInWeek(date: Date, weekStart: Date) {
  const { start, end } = getWeekRange(weekStart)
  const d = dayjs(date)
  return d.isAfter(start.subtract(1, 'ms')) && d.isBefore(end.add(1, 'ms'))
}

export function DashboardFiltersProvider({ children }: { children: React.ReactNode }) {
  const [tripType, setTripType] = useState<IncotermGoupEnum>(IncotermGoupEnum.FOB)
  const [weekStart, setWeekStartState] = useState<Date>(mondayThisWeek)
  const [selectedDay, setSelectedDay] = useState<Date>(mondayThisWeek)
  const [selectedMonth, setSelectedMonth] = useState<Date>(startOfCurrentMonth)
  const [monthlyComplianceStatuses, setMonthlyComplianceStatuses] = useState<
    MonthlyComplianceStatus[]
  >(defaultMonthlyComplianceStatuses)
  const [supplierId, setSupplierId] = useState<number | undefined>(undefined)
  const [carrierId, setCarrierId] = useState<number | undefined>(undefined)

  // When weekStart changes, ensure selectedDay stays within the week
  const setWeekStart = useCallback(
    (newWeekStart: Date) => {
      setWeekStartState(newWeekStart)
      // If selected day is outside the new week, reset to Monday of that week
      if (!isDateInWeek(selectedDay, newWeekStart)) {
        setSelectedDay(dayjs(newWeekStart).isoWeekday(1).startOf('day').toDate())
      }
    },
    [selectedDay],
  )

  const resetFiltersToDefaults = useCallback(() => {
    const monday = mondayThisWeek()
    setTripType(IncotermGoupEnum.FOB)
    setWeekStartState(monday)
    setSelectedDay(monday)
    setSelectedMonth(startOfCurrentMonth())
    setMonthlyComplianceStatuses(defaultMonthlyComplianceStatuses())
    setSupplierId(undefined)
    setCarrierId(undefined)
  }, [])

  const value = useMemo(
    () => ({
      tripType,
      setTripType,
      weekStart,
      setWeekStart,
      selectedDay,
      setSelectedDay,
      selectedMonth,
      setSelectedMonth,
      monthlyComplianceStatuses,
      setMonthlyComplianceStatuses,
      supplierId,
      setSupplierId,
      carrierId,
      setCarrierId,
      resetFiltersToDefaults,
    }),
    [
      tripType,
      weekStart,
      setWeekStart,
      selectedDay,
      selectedMonth,
      monthlyComplianceStatuses,
      supplierId,
      carrierId,
      resetFiltersToDefaults,
    ],
  )

  return (
    <DashboardFiltersContext.Provider value={value}>{children}</DashboardFiltersContext.Provider>
  )
}

export function useDashboardFilters(): DashboardFiltersContextValue {
  const ctx = useContext(DashboardFiltersContext)
  if (!ctx) {
    throw new Error('useDashboardFilters must be used within DashboardFiltersProvider')
  }
  return ctx
}
